import { ConfigProvider } from "antd";
import { useApplicationCurrencies } from "../../../../../containers/ApplicationPage/ApplicationViewContext";
import React from "react";
import { useTranslation } from "react-i18next";
import { Descriptions, Select, Space, Typography } from "antd";
import { numericFormatter } from "react-number-format";
import LocationWithIcon from "../../components/LocationWithIcon";
import FtlSelectedOptionsCell from "./components/FtlSelectedOptionsCell";
import { transportationTypeOptionsKeys } from "../../../../pickers/TransportationTypePicker";
import { importExportOptionsKeys } from "../../../../pickers/ImportExportPicker";
import {
  availabilityOfBatteriesOptionKeys,
  fragileCargoOptionKeys,
  packingOfCargoOptionsKeys,
  stackabilityOfCargoOptionKeys,
} from "../../../../../containers/ApplicationPage/components/ApplicationDraft/components/FormItems/StandardOptionFormItems/constants";
import exhaustiveCheck from "../../../../../utils/exhaustiveCheck";
import { ftlTrailterTypeOptionKeys } from "../../../../pickers/FtlTrailterTypePicker";
import { fwlCarrigeTypeOptionKeys } from "../../../../../containers/ApplicationPage/components/ApplicationDraft/components/Forms/FwlForm/constants";
import LPCustomNumericFormat from "../../../../LongpollControls/LPCustomNumericFormat";
import LPInput from "../../../../LongpollControls/LPInput";
import LPAutoComplete from "../../../../LongpollControls/LPAutoComplete";
import type { ApplicationSummaryBlockProps } from "../../types";

const TransportationDetails = React.memo(({ application, onChange, canEditApplicationFields, onCommit }: ApplicationSummaryBlockProps) => {
  const { t } = useTranslation("translations");
  const currencies = useApplicationCurrencies();
  const getCountLabel = () => {
    switch (application.type) {
      case "FTL":
        return t("trucks_count");
      case "FCL":
        return t("count_containers");
      case "FWL":
        return t("carriage_count");
      case "BULK":
        return t("count_containers_carriagies_tracks");
      case "OVERSIZED_CARGO":
        return t("count_of_container_or_carriage_or_track");
      case "AIR":
      case "LTL":
      case "LCL":
      case "LWL":
      case "RO_RO":
        return null;
      default:
        return exhaustiveCheck(application.type);
    }
  };
  return (
    <ConfigProvider
      theme={{
        token: { fontSize: 12 },
        components: {
          Select: {
            activeBorderColor: "transparent",
            activeOutlineColor: "transparent",
          },
          Input: {
            activeBorderColor: "transparent",
            activeShadow: "none",
            inputFontSize: 12,
            inputFontSizeSM: 12,
            inputFontSizeLG: 12,
            paddingBlock: 0,
            paddingInline: 0,
            paddingBlockSM: 0,
            paddingInlineSM: 0,
            paddingBlockLG: 0,
            paddingInlineLG: 0,
          },
          InputNumber: {
            activeBorderColor: "transparent",
            activeShadow: "none",
            inputFontSize: 12,
            inputFontSizeSM: 12,
            inputFontSizeLG: 12,
            paddingBlock: 0,
            paddingInline: 0,
            paddingBlockSM: 0,
            paddingInlineSM: 0,
            paddingBlockLG: 0,
            paddingInlineLG: 0,
          },
        },
      }}
      select={{ styles: { root: { padding: 0 } } }}
      textArea={{ styles: { textarea: { minHeight: 22 } } }}
      descriptions={{
        styles: {
          label: { padding: "6px 8px" },
          content: { padding: "6px 8px" },
        },
      }}
    >
      <Descriptions
        bordered
        items={[
          ["FTL"].includes(application.type) && {
            label: t("trailer_type"),
            children: canEditApplicationFields ? (
              <Select
                variant="borderless"
                value={application.filter.transportCharacteristicValue}
                options={ftlTrailterTypeOptionKeys.map((x) => ({ label: t(x), value: t(x) }))}
                suffixIcon={null}
                styles={{ popup: { root: { minWidth: 280 } } }}
                onChange={(v) => {
                  onChange({ ...application, filter: { ...application.filter, transportCharacteristicValue: v } });
                  onCommit?.();
                }}
                style={{ width: "100%" }}
              />
            ) : (
              application.filter.transportCharacteristicValue || "-"
            ),
          },
          ["FWL"].includes(application.type) && {
            label: t("carriage_type"),
            children: canEditApplicationFields ? (
              <LPAutoComplete
                variant="borderless"
                value={application.filter.transportCharacteristicValue}
                options={fwlCarrigeTypeOptionKeys.map((x) => ({ label: t(x), value: t(x) }))}
                suffix={null}
                styles={{ popup: { root: { minWidth: 280 } } }}
                onChange={(v) => {
                  onChange({ ...application, filter: { ...application.filter, transportCharacteristicValue: v } });
                  onCommit?.();
                }}
                style={{ width: "100%" }}
              />
            ) : (
              application.filter.transportCharacteristicValue || "-"
            ),
          },
          ["FCL"].includes(application.type) && {
            label: t("container_type"),
            children: application.filter.transportCharacteristicValue || "-",
          },
          ["OVERSIZED_CARGO"].includes(application.type) && {
            label: t("type_of_container_or_carriage_or_track"),
            children: canEditApplicationFields ? (
              <LPInput
                value={application.filter.transportCharacteristicValue}
                variant="borderless"
                onChange={(e) => {
                  onChange({ ...application, filter: { ...application.filter, transportCharacteristicValue: e.target.value } });
                  onCommit?.();
                }}
              />
            ) : (
              application.filter.transportCharacteristicValue || "-"
            ),
          },
          ["FTL"].includes(application.type) && [
            {
              label: t("who_make_export_declaration"),
              children: (
                <FtlSelectedOptionsCell
                  canEdit={canEditApplicationFields}
                  onChange={onChange}
                  onCommit={onCommit}
                  application={application}
                  options={[
                    {
                      value: "LTGF",
                      label: t("forwarder"),
                    },
                    {
                      value: "CARGO_OWNER",
                      label: t("cargo_owner"),
                    },
                  ]}
                />
              ),
            },
            {
              label: t("where_cargo_clearance"),
              children: (
                <>
                  {application.selectedOptions?.includes("BORDER_CROSSING") && (
                    <Space>
                      {t("on_border_crossing")}
                      {application.filter.borderCrossingLocation && (
                        <LocationWithIcon location={application.filter.borderCrossingLocation} />
                      )}
                    </Space>
                  )}
                  {application.selectedOptions?.includes("CUSTOMS_POST") && (
                    <Space>
                      {t("on_custom_post")}
                      {application.filter.customsPostsLocation && <LocationWithIcon location={application.filter.customsPostsLocation} />}
                    </Space>
                  )}
                </>
              ),
            },
            {
              label: t("who_load_cargo"),
              children: (
                <FtlSelectedOptionsCell
                  canEdit={canEditApplicationFields}
                  onChange={onChange}
                  onCommit={onCommit}
                  application={application}
                  options={[
                    {
                      value: "CONSIGNOR",
                      label: t("cargo_shipper"),
                    },
                    {
                      value: "LOADING_REQUIRED",
                      label: t("cargo_loading_required"),
                    },
                  ]}
                />
              ),
            },
            {
              label: t("who_unload_cargo"),
              children: (
                <FtlSelectedOptionsCell
                  canEdit={canEditApplicationFields}
                  onChange={onChange}
                  onCommit={onCommit}
                  application={application}
                  options={[
                    {
                      value: "CONSIGNEE",
                      label: t("cargo_consignee"),
                    },
                    {
                      value: "UNLOADING_REQUIRED",
                      label: t("cargo_unloading_required"),
                    },
                  ]}
                />
              ),
            },
            {
              label: t("how_sling_cargo"),
              children: (
                <FtlSelectedOptionsCell
                  canEdit={canEditApplicationFields}
                  onChange={onChange}
                  onCommit={onCommit}
                  application={application}
                  options={[
                    {
                      value: "SLINGS",
                      label: t("slings"),
                    },
                    {
                      value: "DOUBLE_SLINGS",
                      label: t("double_slings"),
                    },
                  ]}
                />
              ),
            },
            {
              label: t("is_individual_packaging_required"),
              children: (
                <FtlSelectedOptionsCell
                  canEdit={canEditApplicationFields}
                  onChange={onChange}
                  onCommit={onCommit}
                  application={application}
                  options={[
                    {
                      value: "INDIVIDUAL_PACKAGING_NOT_NO",
                      label: t("no"),
                    },
                    {
                      value: "INDIVIDUAL_PACKAGING_YES",
                      label: t("yes"),
                    },
                  ]}
                />
              ),
            },
            {
              label: t("do_need_cargo_insurance"),
              children: (
                <FtlSelectedOptionsCell
                  canEdit={canEditApplicationFields}
                  onChange={onChange}
                  onCommit={onCommit}
                  application={application}
                  options={[
                    {
                      value: "CARGO_INSURANCE_NO",
                      label: t("no"),
                    },
                    {
                      value: "CARGO_INSURANCE_YES",
                      label: t("yes"),
                    },
                  ]}
                />
              ),
            },
            {
              label: t("do_need_customs_clearance"),
              children: (
                <FtlSelectedOptionsCell
                  canEdit={canEditApplicationFields}
                  onChange={onChange}
                  onCommit={onCommit}
                  application={application}
                  options={[
                    {
                      value: "CUSTOMS_CLEARANCE_NO",
                      label: t("no"),
                    },
                    {
                      value: "CUSTOMS_CLEARANCE_YES",
                      label: t("yes"),
                    },
                  ]}
                />
              ),
            },
            {
              label: t("svh_required"),
              children: (
                <FtlSelectedOptionsCell
                  canEdit={canEditApplicationFields}
                  onChange={onChange}
                  onCommit={onCommit}
                  application={application}
                  options={[
                    {
                      value: "SVH_STORAGE_AT_DESTINATION_NO",
                      label: t("no"),
                    },
                    {
                      value: "SVH_STORAGE_AT_DESTINATION_YES",
                      label: t("yes"),
                    },
                  ]}
                />
              ),
            },
            application.type === "FTL" && {
              label: t("do_refrigerator_required"),
              children: canEditApplicationFields ? (
                <Select
                  variant="borderless"
                  value={application.cargoInfo?.isRefrigeratorRequired}
                  styles={{ popup: { root: { minWidth: 100 } } }}
                  options={[
                    {
                      value: true,
                      label: t("yes"),
                    },
                    {
                      value: false,
                      label: t("no"),
                    },
                  ]}
                  suffixIcon={null}
                  onChange={(v) => {
                    onChange({
                      ...application,
                      cargoInfo: { ...application.cargoInfo, isRefrigeratorRequired: v },
                    });
                    onCommit?.();
                  }}
                />
              ) : (
                <Typography.Text style={{ whiteSpace: "pre-wrap" }}>
                  {application.cargoInfo?.isRefrigeratorRequired ? t("yes") : t("no")}
                </Typography.Text>
              ),
            },
            application.type === "FTL" && {
              label: t("temperature_regime"),
              children: canEditApplicationFields ? (
                <LPInput
                  value={application.cargoInfo?.temperatureRegime}
                  variant="borderless"
                  onChange={(e) => {
                    onChange({ ...application, cargoInfo: { ...application.cargoInfo, temperatureRegime: e.target.value } });
                    onCommit?.();
                  }}
                />
              ) : (
                application.cargoInfo?.temperatureRegime || "-"
              ),
            },
          ],
          {
            label: t("cargo_cost"),
            children: (
              <>
                {canEditApplicationFields && (
                  <LPCustomNumericFormat
                    value={application.filter.cost}
                    variant="borderless"
                    thousandSeparator=" "
                    decimalScale={0}
                    allowNegative={false}
                    onChange={(v) => {
                      onChange({ ...application, filter: { ...application.filter, cost: v } });
                      onCommit?.();
                    }}
                  />
                )}
                {!canEditApplicationFields && (
                  <div>
                    {application.filter.cost === undefined
                      ? ""
                      : numericFormatter(`${application.filter.cost}` || "", {
                          thousandSeparator: " ",
                          decimalScale: 0,
                          allowNegative: false,
                          suffix: application.filter.currency?.symbol,
                        })}
                  </div>
                )}
              </>
            ),
          },
          {
            label: t("currency"),
            children: canEditApplicationFields ? (
              <Select
                variant="borderless"
                value={application.filter.currency?.id}
                options={currencies.map((x) => ({ value: x.id, label: x.name }))}
                suffixIcon={null}
                onChange={(v) => {
                  const currency = currencies.find((x) => x.id === v);
                  onChange({ ...application, filter: { ...application.filter, currency } });
                  onCommit?.();
                }}
                style={{ width: "100%" }}
              />
            ) : (
              application.filter.currency?.name || "-"
            ),
          },
          ["AIR", "BULK", "FCL", "FWL", "LCL", "LWL", "OVERSIZED_CARGO", "RO_RO"].includes(application.type) && {
            label: t("transportation_type"),
            children: canEditApplicationFields ? (
              <Select
                variant="borderless"
                value={application.transportationType}
                options={transportationTypeOptionsKeys.map((x) => ({ label: t(x), value: t(x) }))}
                suffixIcon={null}
                onChange={(v) => {
                  onChange({ ...application, transportationType: v });
                  onCommit?.();
                }}
                style={{ width: "100%" }}
              />
            ) : (
              application.transportationType || "-"
            ),
          },
          ["AIR", "BULK", "FCL", "FWL", "LCL", "LWL", "OVERSIZED_CARGO", "LTL"].includes(application.type) && {
            label: t("is_cargo_stackable_or_not"),
            children: canEditApplicationFields ? (
              <LPAutoComplete
                variant="borderless"
                value={application.cargoInfo?.stackabilityOfCargo}
                options={stackabilityOfCargoOptionKeys.map((x) => ({ label: t(x), value: t(x) }))}
                suffix={null}
                onChange={(v) => {
                  onChange({ ...application, cargoInfo: { ...application.cargoInfo, stackabilityOfCargo: v } });
                  onCommit?.();
                }}
                style={{ width: "100%" }}
              />
            ) : (
              application.cargoInfo?.stackabilityOfCargo || "-"
            ),
          },
          {
            label: t("import_export_cargo"),
            children: canEditApplicationFields ? (
              <Select
                variant="borderless"
                value={application.importExportOfCargo}
                options={importExportOptionsKeys.map((x) => ({ label: t(x), value: t(x) }))}
                suffixIcon={null}
                onChange={(v) => {
                  onChange({ ...application, importExportOfCargo: v });
                  onCommit?.();
                }}
                style={{ width: "100%" }}
              />
            ) : (
              application.importExportOfCargo || "-"
            ),
          },
          {
            label: t("how_cargo_will_be_packed"),
            children: canEditApplicationFields ? (
              <LPAutoComplete
                variant="borderless"
                value={application.cargoInfo?.packingOfCargo}
                options={packingOfCargoOptionsKeys.map((x) => ({ value: t(x), label: t(x) }))}
                suffix={null}
                onChange={(v) => {
                  onChange({ ...application, cargoInfo: { ...application.cargoInfo, packingOfCargo: v } });
                  onCommit?.();
                }}
                style={{ width: "100%" }}
              />
            ) : (
              application.cargoInfo?.packingOfCargo || "-"
            ),
          },
          ["BULK", "FCL", "FTL", "FWL", "OVERSIZED_CARGO"].includes(application.type) && {
            label: getCountLabel(),
            children: canEditApplicationFields ? (
              <LPCustomNumericFormat
                value={application.filter.count}
                variant="borderless"
                thousandSeparator=" "
                decimalScale={0}
                allowNegative={false}
                onChange={(v) => {
                  onChange({ ...application, filter: { ...application.filter, count: v } });
                  onCommit?.();
                }}
              />
            ) : (
              application.filter.count || "-"
            ),
          },
          ["AIR", "LCL", "LTL", "LWL"].includes(application.type) && {
            label: t("is_cargo_fragile_or_not"),
            children: canEditApplicationFields ? (
              <LPAutoComplete
                variant="borderless"
                value={application.cargoInfo?.fragileCargo}
                options={fragileCargoOptionKeys.map((x) => ({ label: t(x), value: t(x) }))}
                suffix={null}
                onChange={(v) => {
                  onChange({ ...application, cargoInfo: { ...application.cargoInfo, fragileCargo: v } });
                  onCommit?.();
                }}
                style={{ width: "100%" }}
              />
            ) : (
              application.cargoInfo?.fragileCargo || "-"
            ),
          },
          ["AIR", "FWL", "LCL", "LTL", "LWL"].includes(application.type) && {
            label: t("do_availability_of_batteries"),
            children: canEditApplicationFields ? (
              <LPAutoComplete
                variant="borderless"
                value={application.cargoInfo?.availabilityOfBatteries}
                options={availabilityOfBatteriesOptionKeys.map((x) => ({ label: t(x), value: t(x) }))}
                suffix={null}
                onChange={(v) => {
                  onChange({ ...application, cargoInfo: { ...application.cargoInfo, availabilityOfBatteries: v } });
                  onCommit?.();
                }}
                style={{ width: "100%" }}
              />
            ) : (
              application.cargoInfo?.availabilityOfBatteries || "-"
            ),
          },
          ["AIR", "FTL", "FWL", "LCL", "LTL", "LWL", "BULK", "FCL", "OVERSIZED_CARGO", "RO_RO"].includes(application.type) && {
            label: t("is_cargo_sanctioned"),
            children: canEditApplicationFields ? (
              <Select
                variant="borderless"
                value={application.cargoInfo?.isSanctionsCargo}
                options={[
                  { value: false, label: t("no") },
                  { value: true, label: t("yes") },
                ]}
                suffixIcon={null}
                onChange={(v) => {
                  onChange({ ...application, cargoInfo: { ...application.cargoInfo, isSanctionsCargo: v } });
                  onCommit?.();
                }}
                style={{ width: "100%" }}
              />
            ) : application.cargoInfo?.isSanctionsCargo === undefined ? (
              "-"
            ) : application.cargoInfo.isSanctionsCargo ? (
              t("yes")
            ) : (
              t("no")
            ),
          },
        ]
          .flat(3)
          .filter((item) => !!item)}
      />
    </ConfigProvider>
  );
});
export default TransportationDetails;
